import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import hrmsService, { hrmsKeys, type WorkWeek } from '@/services/hrms.service';
import { WORK_WEEK_OPTIONS } from './hrmsPolicyOptions';

export const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const FALLBACK: WorkWeek[] = WORK_WEEK_OPTIONS.map((o) => ({
  code: o.value,
  label: o.label,
  off_days: [],
  built_in: true,
}));

export function offDaysText(offDays: number[]): string {
  if (!offDays.length) return 'no weekly off';
  return offDays.map((d) => WEEKDAY_OPTIONS[d]?.label.slice(0, 3)).join(', ');
}

/** Built-in plus company work weeks, as Select options and a code → label map. */
export function useWorkWeeks() {
  const { data, isLoading } = useQuery({
    queryKey: hrmsKeys.workWeeks(),
    queryFn: () => hrmsService.listWorkWeeks(),
    staleTime: 5 * 60 * 1000,
  });
  const workWeeks = data?.length ? data : FALLBACK;
  return useMemo(
    () => ({
      workWeeks,
      isLoading,
      options: workWeeks.map((w) => ({
        value: w.code,
        label: w.off_days.length ? `${w.label} (off: ${offDaysText(w.off_days)})` : w.label,
      })),
      labelByCode: new Map(workWeeks.map((w) => [w.code, w.label])),
    }),
    [workWeeks, isLoading]
  );
}
